import { PageLoadingSpinner, appConfig, makeConcealable, makeValidable } from '@ami/react/core';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { findDOMNode } from 'react-dom';
import { ProductVisibility } from '../../products/product';
import { ProductConfiguration } from '../../products/productConfiguration';
import { ProductGroupTypes, ProductGroupVisibility } from '../../products/productGroup';
import { productGroupDimensionCategory } from '../../products/productGroupDimensionCategoryType';
import LeadTimeWarnings from '../../quotes/LeadTimeWarnings';
import ProductAndOptionsWarnings from '../../quotes/ProductAndOptionsWarnings';
import ProductTineConfiguration from '../../quotes/ProductTineConfiguration';
import QuoteProductDetail from '../../quotes/entities/quoteProductDetail';
import { ThumbBucketProfileToothStyleType } from '../../quotes/entities/thumbBucketProfileToothStyleType';
import {
  QuoteProductDimension,
  QuoteProductDto,
  couplerDecision,
} from '../../quotes/otherQuoteEntities';
import { Quote } from '../../quotes/quote';
import { QuoteProductConfiguration } from '../../quotes/quoteProductConfiguration';
import ProductThumbSelectionComponent from '../../quotes/step3/ProductThumbSelectionComponent';
import QuoteSharedDocumentsListComponent from '../../quotes/view/quote-documents/QuoteSharedDocumentsListComponent';
import ThumbBucketProfileFilter from '../../thumb-bucket-profiles/ThumbBucketProfileFilter';
import thumbBucketProfileService from '../../thumb-bucket-profiles/thumbBucketProfileService';
import ThumbBucketLocationType from '../entities/thumbBucketLocationType';
import { quoteProductType } from '../otherQuoteEntities';
import LeadTimeBreakdownComponent from './LeadTimeBreakdownComponent';
import MachineInformationContainer from './MachineInformationContainer';
import ProductDetailComponent from './ProductDetailComponent';
import { ProductOptionSetsContainer } from './ProductOptionSetsContainer';
import { ProductSelectionComponent } from './ProductSelectionComponent';
import StockItemTermsComponent from './StockItemTermsComponent';
import { QuoteInventoryItemViewModel } from './components/QuoteInventoryItemViewModel';

const ValidableProductOptionSetsContainer = makeValidable(
  ProductOptionSetsContainer
);

class ProductAndOptionsSelectionComponent extends React.Component {
  constructor(props) {
    super(props);
    this.validableRef = React.createRef();
    this.summaryRef = React.createRef();
    this.selectModelRef = React.createRef();
  }

  static propTypes = {
    quoteProductDto: PropTypes.instanceOf(QuoteProductDto).isRequired,
    submitting: PropTypes.bool.isRequired,
    onQuoteProductDtoChange: PropTypes.func.isRequired,
    quoteProductConfiguration: PropTypes.instanceOf(QuoteProductConfiguration)
      .isRequired,
    productGroup: PropTypes.instanceOf(Object).isRequired,
    onSaveProduct: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    productConfiguration: PropTypes.instanceOf(ProductConfiguration).isRequired,
    isOrderFormMode: PropTypes.bool,
    machineMissing: PropTypes.bool.isRequired,
    couplerMissing: PropTypes.bool.isRequired,
    isAmiPriceLookupMode: PropTypes.bool.isRequired,
    hasAgreedStockTerms: PropTypes.bool.isRequired,
    onHasAgreedStockTermsChange: PropTypes.func.isRequired,
    quote: PropTypes.instanceOf(Quote).isRequired,
    documents: PropTypes.array,
    onloadSharedDocuments: PropTypes.func.isRequired,
    showPricingSpinner: PropTypes.bool.isRequired,
    showLeadTimeCalculationSpinner: PropTypes.bool.isRequired,
    selectedQuoteProductType: PropTypes.number.isRequired,
    selectedInventoryItem: PropTypes.instanceOf(QuoteInventoryItemViewModel),
    productSelected: PropTypes.func.isRequired,
  };

  state = {
    showValidations: false,
    products: [],
    thumbBucketProfileId: null,
    gettingProducts: true,
    showIncompatibleThumbWarnings: false
  };

  async componentDidMount() {
    const { productGroup, onQuoteProductDtoChange, quoteProductDto } =
      this.props;
    if (
      (productGroup.type === ProductGroupTypes.StickPivotThumb.id ||
        productGroup.type === ProductGroupTypes.ExtendableStickThumb.id ||
        productGroup.type === ProductGroupTypes.ProLinkThumb.id ||
        productGroup.type === ProductGroupTypes.WeldOnThumb.id) &&
      !quoteProductDto.detail
    ) {
      onQuoteProductDtoChange({ detail: new QuoteProductDetail() });
    }
    await this.getProducts().then((products) => {
      this.setState({ products }, () => {
        this.fisnishGettingProducts();
      });
    });
  }

  async componentDidUpdate(prevProps) {
    const { productGroup, onQuoteProductDtoChange, quoteProductDto } =
      this.props;
    if (prevProps.quoteProductDto !== this.props.quoteProductDto) {
      if (
        (productGroup.type === ProductGroupTypes.StickPivotThumb.id ||
          productGroup.type === ProductGroupTypes.ExtendableStickThumb.id ||
          productGroup.type === ProductGroupTypes.ProLinkThumb.id ||
          productGroup.type === ProductGroupTypes.WeldOnThumb.id) &&
        !quoteProductDto.detail
      ) {
        onQuoteProductDtoChange({ detail: new QuoteProductDetail() });
      }
      if (
        prevProps.quoteProductDto.detail !==
        this.props.quoteProductDto.detail ||
        prevProps.quoteProductDto.productId !=
        this.props.quoteProductDto.productId ||
        prevProps.quoteProductDto.productGroupId !=
        this.props.quoteProductDto.productGroupId
      ) {
        this.setState({ gettingProducts: true });
        await this.getProducts().then((products) => {
          this.setState({ products }, () => {
            this.fisnishGettingProducts(
              prevProps.quoteProductDto.productId !=
              this.props.quoteProductDto.productId
            );
          });
        });

        if (this.props.quoteProductDto.productId) {
          var elements =
            this.validableRef.current.containerRef.current.querySelectorAll(
              'div.hide-validations .invalid'
            );
          if (elements.length > 0) {
            elements[0].scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          } else {
            this.validableRef.current.containerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }
      }
    }
  }

  fisnishGettingProducts(skipScroll) {
    if (!skipScroll) {
      const node = findDOMNode(this.selectModelRef);
      if (node) {
        node.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
    this.setState({ gettingProducts: false });
  }

  getSelectedProduct = () =>
    this.props.quoteProductConfiguration.products.find(
      (x) => x.id === this.props.quoteProductDto.productId
    );

  getSelectedInventoryItem = () =>
    this.props.quoteProductConfiguration.inventoryItems.find(
      (x) => x.id === this.props.quoteProductDto.inventoryItemId
    );

  isMachineSpecified = () => !!this.props.quote.step1.machineId;

  isLuggingSpecified = () => {
    var quoteAttachmentPoint = this.props.quote.attachmentPoints.find(
      (x) => x.id === this.props.productGroup.attachmentPointId
    );
    return (
      quoteAttachmentPoint.couplerDecision &&
      quoteAttachmentPoint.couplerDecision !== couplerDecision.notSpecified
    );
  };

  isVisibilityProductGroup() {
    const { productGroup } = this.props;
    const product = this.getSelectedProduct();

    if (!product) return false;

    const isDealerApp = appConfig.isDealerApp;
    const isManagementApp = appConfig.isManagementApp;

    var dealerVisible =
      isDealerApp &&
      ProductGroupVisibility[productGroup.visibility] !==
      ProductGroupVisibility.VisibleInternalRestrictedPublic &&
      ProductVisibility[product.visibility] !==
      ProductGroupVisibility.VisibleInternalRestrictedPublic;

    return dealerVisible || isManagementApp;
  }
  async GetThumbBucketProfile(style, carrierId, weightCategoryId, width) {
    var thumbBucketProfile = null;
    await thumbBucketProfileService
      .search(
        new ThumbBucketProfileFilter({
          style: style,
          carrierId: carrierId,
          weightCategoryId: weightCategoryId,
          width: width,
        })
      )
      .then((thumbBucketProfiles) => {
        if (thumbBucketProfiles.length > 0) {
          thumbBucketProfile = thumbBucketProfiles[0];
        }
      });

    return thumbBucketProfile;
  }

  getProductBucketOnSameQuote = () => {
    const { quoteProductDto, quote } = this.props;
    const { detail } = quoteProductDto;
    if (detail && detail.bucketQuoteProductId) {
      return quote.products.find((x) => x.id === detail.bucketQuoteProductId);
    }
    return null;
  };

  getProducts = async () => {
    const { quoteProductConfiguration, quoteProductDto, quote } = this.props;
    const { productGroupId, productId, detail } = quoteProductDto;
    var thumbBucketProfileId = null;
    var toothCount = null;
    var toothStyle = null;

    if (detail && detail.bucketQuoteProductId) {
      const bucketQuoteProduct = quote.products.find(
        (x) => x.id === detail.bucketQuoteProductId
      );
      if (!bucketQuoteProduct) return [];
      const productGroup = quoteProductConfiguration.productGroups.find(
        (x) => x.id === bucketQuoteProduct.productGroupId
      );
      const widthDimension = bucketQuoteProduct.dimensions.find(
        (x) => x.category === productGroupDimensionCategory.Width
      );
      const teethDimension = bucketQuoteProduct.dimensions.find(
        (x) => x.category === productGroupDimensionCategory.Teeth
      );

      if (teethDimension) {
        toothCount = teethDimension.value;
      }
      var style = null;
      switch (productGroup.type) {
        case ProductGroupTypes.StandardDiggingBucket.id:
        case ProductGroupTypes.RecessedDiggingBucket.id:
          toothStyle = ThumbBucketProfileToothStyleType.Standard;
          style = 'Digging';
          break;
        case ProductGroupTypes.SpadeToothDiggingBucket.id:
        case ProductGroupTypes.SpadeToothRecessedDiggingBucket.id:
          toothStyle = ThumbBucketProfileToothStyleType.Flare;
          style = 'Digging';
          break;
        case ProductGroupTypes.DitchingBucket.id:
          style = 'Ditching';
          toothStyle = null;
          toothCount = null;
          break;
        case ProductGroupTypes.TiltDitchingBucket.id:
          style = 'TiltDitching';
          toothStyle = null;
          toothCount = null;
          break;
        default:
          break;
      }

      const thumbBucketProfile = await this.GetThumbBucketProfile(
        style,
        bucketQuoteProduct.carrierId,
        bucketQuoteProduct.weightCategoryId,
        widthDimension ? widthDimension.originalValue : null
      );
      if (thumbBucketProfile) {
        thumbBucketProfileId = thumbBucketProfile.id;
      } else {
        this.setState({ thumbBucketProfileId: null });
        return [];
      }
    }

    if (detail && detail.existingBucket) {
      thumbBucketProfileId = detail.existingBucket.thumbBucketProfileId;
      toothCount = detail.existingBucket.toothCount;
      toothStyle = detail.existingBucket.toothStyle;
    }

    this.setState({ thumbBucketProfileId });
    const products = quoteProductConfiguration.getProductsByFilter(
      productGroupId,
      appConfig.isDealerApp,
      productId,
      thumbBucketProfileId,
      toothCount,
      toothStyle
    );
    return products;
  };

  render() {
    const {
      productConfiguration,
      quoteProductConfiguration,
      quoteProductDto,
      onSaveProduct,
      onQuoteProductDtoChange,
      onCancel,
      productGroup,
      submitting,
      isOrderFormMode,
      machineMissing,
      couplerMissing,
      isAmiPriceLookupMode,
      hasAgreedStockTerms,
      onHasAgreedStockTermsChange,
      quote,
      documents,
      onloadSharedDocuments,
      showPricingSpinner,
      showLeadTimeCalculationSpinner,
      selectedQuoteProductType,
      selectedInventoryItem,
      productSelected
    } = this.props;
    const { products, thumbBucketProfileId, gettingProducts, showIncompatibleThumbWarnings } = this.state;

    const isEditProductMode = !!quoteProductDto.id;
    const { productGroupId, originalProduct } = quoteProductDto;
    const productOptionSets =
      quoteProductConfiguration.getProductOptionSetsByProductGroupId(
        productGroupId
      );
    const honorPricing =
      isEditProductMode &&
      originalProduct &&
      quoteProductDto.productId === originalProduct.productId;
    const quoteAttachmentPoint = quote.attachmentPoints.find(
      (x) => x.id === productGroup.attachmentPointId
    );
    const couplerDecision = quoteAttachmentPoint
      ? quoteAttachmentPoint.couplerDecision
      : null;
    const couplerOilReady = quoteAttachmentPoint
      ? quoteAttachmentPoint.couplerOilReady
      : null;
    const couplerStyle =
      quoteAttachmentPoint && quoteAttachmentPoint.couplerStyleId
        ? quoteProductConfiguration.couplerStyles.find(
          (x) => x.id === quoteAttachmentPoint.couplerStyleId
        )
        : undefined;

    const thumbCompatibleMissing =
      products.length === 0 &&
      !!quoteProductDto.detail &&
      (!quoteProductDto.detail.isEdit ||
        !!quoteProductDto.detail.bucketQuoteProductId);

    const visibleThumbConfigurator =
      productGroup.type === ProductGroupTypes.StickPivotThumb.id ||
      productGroup.type === ProductGroupTypes.ExtendableStickThumb.id ||
      productGroup.type === ProductGroupTypes.ProLinkThumb.id ||
      productGroup.type === ProductGroupTypes.WeldOnThumb.id;

    const visibleSelectModel =
      !thumbCompatibleMissing &&
      ((productGroup.type !== ProductGroupTypes.StickPivotThumb.id &&
        productGroup.type !== ProductGroupTypes.ExtendableStickThumb.id &&
        productGroup.type !== ProductGroupTypes.ProLinkThumb.id &&
        productGroup.type !== ProductGroupTypes.WeldOnThumb.id) ||
        (visibleThumbConfigurator &&
          quoteProductDto.detail &&
          (quoteProductDto.detail.bucketQuoteProductId ||
            (!quoteProductDto.detail.isEdit &&
              ((quoteProductDto.detail.existingBucketSpecsAvailable != null &&
                quoteProductDto.detail.existingBucketSpecsAvailable &&
                quoteProductDto.detail.existingBucket != null) ||
                (quoteProductDto.detail.existingBucketSpecsAvailable != null &&
                  !quoteProductDto.detail.existingBucketSpecsAvailable &&
                  quoteProductDto.detail
                    .existingBucketSpecsNotAvailableAcknowledgedByUserId !=
                  null))))));

    const visibleTineConfiguration =
      !isAmiPriceLookupMode &&
      !thumbCompatibleMissing &&
      quoteProductDto.productId &&
      visibleThumbConfigurator &&
      quoteProductDto.detail &&
      (quoteProductDto.detail.bucketQuoteProductId ||
        (!quoteProductDto.detail.isEdit &&
          quoteProductDto.detail.existingBucketSpecsAvailable != null &&
          quoteProductDto.detail.existingBucketSpecsAvailable &&
          quoteProductDto.detail.existingBucket));

    const noBucketSpecsAvailable =
      visibleThumbConfigurator &&
      !thumbCompatibleMissing &&
      !!quoteProductDto.detail &&
      !quoteProductDto.detail.isEdit &&
      quoteProductDto.detail.noBucketSpecsAvailable();

    const alertFasterLeadTimes =
      visibleThumbConfigurator === true &&
      visibleSelectModel === true &&
      !!quoteProductDto.detail &&
      quoteProductDto.detail.bucketLocation === ThumbBucketLocationType.ExistingQuote;

    const thumbBucketLocation = quoteProductDto.detail?.bucketLocation ?? null;
    const thumbExistingBucketBrand = !!quoteProductDto.detail && quoteProductDto.detail.existingBucket ? quoteProductDto.detail.existingBucket.brand : null;

    let productOptionSetsVisible = false;
    let productDetailsVisible = false;
    let productSelectAModelHeadingVisible = false;
    let leadTimeWarningVisible = false;
    let productSelectionVisible = false;
    let productLoadingSpinnerVisible = false;

    if (selectedQuoteProductType === quoteProductType.inventoryOnly) {
      productOptionSetsVisible = visibleThumbConfigurator ? !!quoteProductDto.productId : (!!quoteProductDto.productId && !!quoteProductDto.inventoryItemId);
      productDetailsVisible = visibleThumbConfigurator ? (visibleSelectModel && !gettingProducts) : (!!quoteProductDto.productId && !!quoteProductDto.inventoryItemId && visibleSelectModel && !gettingProducts);
    } else {
      productOptionSetsVisible = !!quoteProductDto.productId;
      productDetailsVisible = visibleSelectModel && !gettingProducts;
      productSelectAModelHeadingVisible = visibleSelectModel && !gettingProducts;
      leadTimeWarningVisible = visibleSelectModel && !gettingProducts;
      productSelectionVisible = visibleSelectModel && !gettingProducts;
      productLoadingSpinnerVisible = visibleSelectModel && gettingProducts;
    }

    return (
      <div className="row my-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              {visibleThumbConfigurator && (
                <div className="hr-divider">
                  <h3 className="hr-divider-content hr-divider-heading">
                    Configure thumb
                  </h3>
                </div>
              )}
              {visibleThumbConfigurator && (!couplerMissing || isAmiPriceLookupMode) && quoteProductDto.detail && (
                <ProductThumbSelectionComponent
                  entity={quoteProductDto.detail}
                  onUpdate={(detail) => onQuoteProductDtoChange({ detail })}
                  quoteProductConfiguration={quoteProductConfiguration}
                  quote={quote}
                  quoteProductDto={quoteProductDto}
                  onQuoteProductDtoChange={onQuoteProductDtoChange}
                  isAmiPriceLookupMode={isAmiPriceLookupMode}
                  onChangeBucketDetails={(isEdit) => {
                    if (isEdit) {
                      onQuoteProductDtoChange({ productId: null, inventoryItemId: null }, true);
                      onHasAgreedStockTermsChange(false);
                      this.setState({ showIncompatibleThumbWarnings: false });
                    }
                  }}
                  onFinishBucketConfiguration={async (completed) => {
                    if (!completed || selectedQuoteProductType !== quoteProductType.inventoryOnly)
                      return;

                    await this.getProducts().then((products) => {
                      const productId = selectedInventoryItem.productId;
                      const inventoryItemId = selectedInventoryItem.id;
                      const hasProduct = products.find(x => x.id === productId);
                      if (productId && inventoryItemId && hasProduct) {
                        onQuoteProductDtoChange({ productId, inventoryItemId, dimensions: QuoteProductDimension.getProductDimensions(productGroup, products.find((x) => x.id === productId)) }, true);
                        onHasAgreedStockTermsChange(false);
                        this.setState({ showIncompatibleThumbWarnings: false });
                      } else {
                        onQuoteProductDtoChange({ productId: null, inventoryItemId: null }, true);
                        onHasAgreedStockTermsChange(false);
                        this.setState({ showIncompatibleThumbWarnings: true })
                      }
                    });
                  }}
                />
              )}

              <div ref={(el) => (this.selectModelRef = el)}>
                {productSelectAModelHeadingVisible && (
                  <div className="hr-divider">
                    <h3 className="hr-divider-content hr-divider-heading">
                      Select a Model
                    </h3>
                  </div>
                )}
              </div>

              {leadTimeWarningVisible && (
                <LeadTimeWarnings
                  machineMissing={machineMissing}
                  couplerMissing={couplerMissing}
                  fullWidth={true}
                />
              )}

              <ProductAndOptionsWarnings
                thumbCompatibleMissing={thumbCompatibleMissing}
                couplerMissing={
                  visibleThumbConfigurator &&
                  couplerMissing &&
                  !isAmiPriceLookupMode
                }
                noBucketSpecsAvailable={noBucketSpecsAvailable}
                alertFasterLeadTimes={alertFasterLeadTimes}
                fullWidth={true}
              />

              {selectedQuoteProductType === quoteProductType.inventoryOnly && showIncompatibleThumbWarnings && (
                <div className="row justify-content-center text-center">
                  <div className="banner-container">
                    <div className="alert alert-warning justify-content-center">
                      <i className="fa fa-info-circle fa-lg text-danger me-1"></i>
                      Unfortunately the bucket you have is not compatible with the selected thumb. Please select a different thumb, or change the bucket specifications to continue.
                    </div>
                  </div>
                </div>
              )}

              {productLoadingSpinnerVisible && <PageLoadingSpinner />}

              {productSelectionVisible === true && (
                <ProductSelectionComponent
                  productId={quoteProductDto.productId}
                  inventoryItemId={quoteProductDto.inventoryItemId}
                  onProductSelected={(productId, inventoryItemId) => {
                    onQuoteProductDtoChange(
                      {
                        productId,
                        inventoryItemId,
                        dimensions: QuoteProductDimension.getProductDimensions(
                          productGroup,
                          products.find((x) => x.id === productId)
                        ),
                      },
                      true
                    );
                    onHasAgreedStockTermsChange(false);
                    productSelected(productId, inventoryItemId);
                  }}
                  productGroup={productGroup}
                  products={products}
                  inventoryItems={quoteProductConfiguration.inventoryItems.filter(
                    (x) => x.productGroupId === productGroupId
                  )}
                  noBucketSpecsAvailable={noBucketSpecsAvailable}
                  thumbBucketLocation={thumbBucketLocation}
                  thumbExistingBucketBrand={thumbExistingBucketBrand}
                />
              )}

              {quoteProductDto.productId && !isEditProductMode && !isOrderFormMode && !isAmiPriceLookupMode && selectedQuoteProductType === quoteProductType.allProducts && (
                <MachineInformationContainer
                  quote={quote}
                  quoteProductDto={quoteProductDto}
                  isEditProductMode={isEditProductMode}
                  onQuoteProductDtoChange={(inventoryItemId) =>
                    onQuoteProductDtoChange({ inventoryItemId })
                  }
                  quoteProductConfiguration={quoteProductConfiguration}
                  show={!!quoteProductDto.inventoryItemId}
                  onloadSharedDocuments={onloadSharedDocuments}
                />
              )}
              {quoteProductDto.inventoryItemId &&
                !isEditProductMode &&
                !isOrderFormMode &&
                !isAmiPriceLookupMode && (
                  <StockItemTermsComponent
                    hasAgreedStockTerms={hasAgreedStockTerms}
                    onHasAgreedStockTermsChange={onHasAgreedStockTermsChange}
                  />
                )}
              {productOptionSetsVisible && (
                <ValidableProductOptionSetsContainer
                  showValidations={this.state.showValidations}
                  ref={this.validableRef}
                  isEditProductMode={isEditProductMode}
                  productGroup={productGroup}
                  couplerDecision={couplerDecision}
                  couplerOilReady={couplerOilReady}
                  couplerStyle={couplerStyle}
                  selectedProductOptions={quoteProductDto.productOptions}
                  onProductChange={(updateObject, loadDocuments) =>
                    onQuoteProductDtoChange(updateObject, loadDocuments)
                  }
                  notes={quoteProductDto.notes}
                  manufacturingInstructions={quoteProductDto.manufacturingInstructions}
                  paintColor={quoteProductDto.paintColor}
                  selectedProduct={this.getSelectedProduct()}
                  selectedInventoryItem={this.getSelectedInventoryItem()}
                  isInventoryMode={selectedQuoteProductType === quoteProductType.inventoryOnly || !!quoteProductDto.inventoryItemId}
                  productOptionSets={productOptionSets}
                  productConfiguration={productConfiguration}
                  preferredBrandId={quoteProductConfiguration.preferredBrandId}
                  preferredSeriesId={
                    quoteProductConfiguration.preferredSeriesId
                  }
                  onLastOptionSelected={() => {
                    const node = findDOMNode(this.summaryRef);
                    if (node && window.innerWidth < 760) {
                      node.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                      });
                    }
                  }}
                  originalProductOptions={
                    honorPricing ? originalProduct.productOptions : []
                  }
                  hideLeadTimeAndPrices={!this.isVisibilityProductGroup()}
                />
              )}
              {visibleTineConfiguration && (
                <div className="hr-divider">
                  <h3 className="hr-divider-content hr-divider-heading">
                    Tine Configuration
                  </h3>
                </div>
              )}
              {visibleTineConfiguration && (
                <ProductTineConfiguration
                  weightCategoryId={quote.step1.weightCategoryId}
                  thumbBucketProfileId={thumbBucketProfileId}
                  couplerStyleId={quoteAttachmentPoint.couplerStyleId}
                  productThumb={quoteProductDto}
                  productBucket={this.getProductBucketOnSameQuote()}
                  onUpdate={(detail) => onQuoteProductDtoChange({ detail })}
                  entity={quoteProductDto.detail}
                />
              )}
            </div>

            {productDetailsVisible === true && (
              <div className="col-lg-4" ref={(el) => (this.summaryRef = el)}>
                <div className="summary-container">
                  <div className="hr-divider">
                    <h3 className="hr-divider-content hr-divider-heading">
                      Summary
                    </h3>
                  </div>
                  <ProductDetailComponent
                    productGroup={productGroup}
                    quoteProductDto={quoteProductDto}
                    onQuoteProductDtoChange={onQuoteProductDtoChange}
                    onSaveProduct={() => {
                      this.setState({ showValidations: true }, () => {
                        if (this.validableRef.current.isValid()) {
                          onSaveProduct();
                        }
                      });
                    }}
                    submitting={submitting}
                    onCancel={onCancel}
                    submitEnabled={
                      (!quoteProductDto.inventoryItemId &&
                        (!visibleTineConfiguration ||
                          !!quoteProductDto.detail.tineDiagram)) ||
                      isOrderFormMode ||
                      ((hasAgreedStockTerms || isEditProductMode) &&
                        this.isMachineSpecified() &&
                        this.isLuggingSpecified())
                    }
                    scrollOnShow={productOptionSets.length === 0}
                    productConfiguration={productConfiguration}
                    productOptionSets={productOptionSets}
                    isOrderFormMode={isOrderFormMode}
                    isAmiPriceLookupMode={isAmiPriceLookupMode}
                    inventoryItem={this.getSelectedInventoryItem()}
                    visibilityProductGroup={this.isVisibilityProductGroup()}
                    showPricingSpinner={showPricingSpinner}
                    showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
                  />
                  <br />
                  {documents && (
                    <div className="hr-divider">
                      <h3 className="hr-divider-content hr-divider-heading">
                        Documents
                      </h3>
                    </div>
                  )}
                  {documents && (
                    <QuoteSharedDocumentsListComponent documents={documents} />
                  )}

                  <br />
                  {appConfig.isManagementApp && quoteProductDto.productId && quoteProductDto.leadTime && (
                    <Fragment>
                      <div className="hr-divider">
                        <h3 className="hr-divider-content hr-divider-heading">
                          Lead Time Details
                        </h3>
                      </div>
                      <LeadTimeBreakdownComponent
                        quoteProduct={quoteProductDto}
                        quoteProductConfiguration={quoteProductConfiguration}
                        showLeadTimeCalculationSpinner={showLeadTimeCalculationSpinner}
                      />
                    </Fragment>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default makeConcealable(ProductAndOptionsSelectionComponent);
